// Visual sprayer picker for the build_car_kit "sprayer" axis. Same
// shape as SizeSilhouetteGallery — one tile per chip with the
// merchant-curated sprayer product photo. Gracefully degrades to a
// label-only chip-style tile if the image is missing (so we don't
// block the picker for the Ultimate Sprayer until that photo is added).

import { useState } from "react"

interface SprayerChip {
  value: string
  label: string
  helperText?: string
}

interface Props {
  chips: SprayerChip[]
  priceDelta?: Record<string, number>
  onSelect: (chipLabel: string) => void
}

// Merchant-curated sprayer images. Keys match the chip `value` emitted
// by pickNextStep in src/lib/chat/build-car-kit.ts. "have" maps to the
// "No Sprayer" image because the customer already has one — the photo
// makes the "no included sprayer" option visually distinct from the
// G-Force/Ultimate tiles.
const SPRAYER_IMAGE_URLS: Record<string, string> = {
  have: "https://cdn.shopify.com/s/files/1/0973/6008/files/No_Sprayer_3dc073bc-3515-41ee-bbe4-027ce8faf6b3.webp",
  "g-force": "https://cdn.shopify.com/s/files/1/0973/6008/files/G_Force.webp",
  ultimate: "https://cdn.shopify.com/s/files/1/0973/6008/files/Ultimate.webp",
}

function imageUrlFor(value: string): string | null {
  return SPRAYER_IMAGE_URLS[value.trim().toLowerCase()] ?? null
}

export function SprayerGallery({ chips, priceDelta, onSelect }: Props) {
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          opacity: 0.6,
          marginBottom: 10,
          textAlign: "center",
        }}
      >
        Tap a sprayer to pick
      </div>
      <div
        style={{
          display: "grid",
          // Responsive: 3-up on desktop, 2-up on mobile. 160px minmax
          // needed 330px to fit 2-up but Pearl's chat bubble is 86% of
          // a 393px viewport → gallery slot only ~290px → collapsed to
          // 1-up. Dropped to 125px so 2-up fits even on a 320px iPhone
          // SE viewport. Desktop canvas pane (~600px) still gives 3-up.
          // Caught 2026-06-03 — second mobile pass.
          gridTemplateColumns: "repeat(auto-fit, minmax(125px, 1fr))",
          gap: 10,
        }}
      >
      {chips.map((c) => {
        const imageUrl = imageUrlFor(c.value)
        const delta = priceDelta?.[c.value]
        return (
          <button
            key={c.value}
            type="button"
            onClick={() => onSelect(c.label)}
            title={c.helperText}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 8,
              padding: "10px 10px 8px",
              borderRadius: 14,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "inherit",
              cursor: "pointer",
              font: "inherit",
              textAlign: "left",
              transition:
                "background 120ms ease, border-color 120ms ease, transform 120ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(109, 195, 247, 0.12)"
              e.currentTarget.style.borderColor = "rgba(109, 195, 247, 0.55)"
              e.currentTarget.style.boxShadow = "0 0 0 3px rgba(109,195,247,0.18)"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.05)"
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)"
              e.currentTarget.style.boxShadow = "none"
            }}
          >
            <div
              style={{
                width: "100%",
                aspectRatio: "1 / 1",
                background:
                  "radial-gradient(120% 100% at 50% 0%, #ffffff 0%, #f3f5f8 70%, #e6eaf0 100%)",
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 8,
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6)",
                overflow: "hidden",
              }}
            >
              <SprayerImageOrPlaceholder imageUrl={imageUrl} label={c.label} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>{c.label}</span>
              {delta != null && (
                <span style={{ opacity: 0.7, fontSize: 11.5 }}>
                  +${(delta / 100).toFixed(0)}
                </span>
              )}
            </div>
            {/* helperText intentionally NOT rendered here — for sprayers it
              * just repeats the variant name, which read as a duplicate under
              * the bold label. The label above is enough. */}
          </button>
        )
      })}
      </div>
    </div>
  )
}

function SprayerImageOrPlaceholder({
  imageUrl,
  label,
}: {
  imageUrl: string | null
  label: string
}) {
  const [failed, setFailed] = useState(false)
  if (imageUrl && !failed) {
    return (
      <img
        src={imageUrl}
        alt={label}
        loading="lazy"
        onError={() => setFailed(true)}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          display: "block",
        }}
      />
    )
  }
  // Placeholder for sprayers without an image yet (e.g., Ultimate
  // Sprayer photo not uploaded). Renders a soft-glyph spray icon so
  // the tile doesn't look broken.
  return (
    <svg
      viewBox="0 0 64 64"
      style={{ width: "55%", height: "auto", color: "#2b3a55" }}
      aria-hidden="true"
    >
      <rect x="16" y="18" width="22" height="34" rx="3" fill="currentColor" />
      <rect x="38" y="22" width="14" height="14" rx="2" fill="currentColor" />
      <circle cx="56" cy="29" r="2" fill="#6dc3f7" />
      <circle cx="60" cy="22" r="1.5" fill="#6dc3f7" opacity="0.7" />
      <circle cx="62" cy="34" r="1.2" fill="#6dc3f7" opacity="0.6" />
      <rect x="20" y="10" width="14" height="10" rx="2" fill="currentColor" />
    </svg>
  )
}
